import type { ReactNode } from "react";

type Card = {
  title: string;
  blurb: string;
  sql: ReactNode;
  out: { label: string; value: string; delta: string };
};

export function WhatYouCanBuild() {
  const cards: Card[] = [
    {
      title: "Signal smoothing",
      blurb: "Remove sensor noise with a moving average.",
      sql: (
        <>
          <span className="cmt">-- smooth raw sensor noise</span>
          {"\n"}
          <span className="kw">CREATE MATERIALIZED VIEW</span> pressure_smooth{" "}
          <span className="kw">AS</span>
          {"\n"}
          <span className="kw">SELECT</span> device_id,
          {"\n"}
          {"       "}tag_path,
          {"\n"}
          {"       "}
          <span className="fn">MOVING_AVERAGE</span>(value,{" "}
          <span className="num">20</span>){" "}
          <span className="kw">AS</span> smoothed
          {"\n"}
          <span className="kw">FROM</span> tag_values;
        </>
      ),
      out: { label: "pressure · ch1", value: "4.82 bar", delta: "±0.01" },
    },
    {
      title: "Thermal drift",
      blurb: "Short and long moving baselines per zone, with deviation alerts.",
      sql: (
        <>
          <span className="cmt">-- drift against a slow baseline</span>
          {"\n"}
          <span className="kw">CREATE MATERIALIZED VIEW</span> zone_drift{" "}
          <span className="kw">AS</span>
          {"\n"}
          <span className="kw">SELECT</span> unit_id, zone,
          {"\n"}
          {"       "}
          <span className="fn">MOVING_AVERAGE</span>(temp,{"  "}
          <span className="num">60</span>){"  "}
          <span className="kw">AS</span> temp_fast,
          {"\n"}
          {"       "}
          <span className="fn">MOVING_AVERAGE</span>(temp,{" "}
          <span className="num">1440</span>){" "}
          <span className="kw">AS</span> temp_slow,
          {"\n"}
          {"       "}temp_fast - temp_slow{"     "}
          <span className="kw">AS</span> drift
          {"\n"}
          <span className="kw">FROM</span> temps
          {"\n"}
          <span className="kw">GROUP BY</span> unit_id, zone;
        </>
      ),
      out: { label: "unit-14 · z3", value: "+2.4 °C", delta: "↑ crossing" },
    },
    {
      title: "Bearing fault bands",
      blurb: "Frequency-band energy for bearing components, with moving baseline alerts.",
      sql: (
        <>
          <span className="cmt">-- BPFO / BPFI filter stage</span>
          {"\n"}
          <span className="kw">CREATE MATERIALIZED VIEW</span> fault_bands{" "}
          <span className="kw">AS</span>
          {"\n"}
          <span className="kw">SELECT</span> device_id, channel_id,
          {"\n"}
          {"       "}
          <span className="fn">IIR</span>(amplitude,
          {"\n"}
          {"           "}
          <span className="kw">ARRAY</span>[<span className="num">0.00723</span>,{" "}
          <span className="num">0</span>, -<span className="num">0.00723</span>],
          {"\n"}
          {"           "}
          <span className="kw">ARRAY</span>[<span className="num">1</span>, -
          <span className="num">1.98423</span>,{" "}
          <span className="num">0.98554</span>]){" "}
          <span className="kw">AS</span> bpfo,
          {"\n"}
          {"       "}
          <span className="fn">IIR</span>(amplitude,
          {"\n"}
          {"           "}
          <span className="kw">ARRAY</span>[<span className="num">0.00908</span>,{" "}
          <span className="num">0</span>, -<span className="num">0.00908</span>],
          {"\n"}
          {"           "}
          <span className="kw">ARRAY</span>[<span className="num">1</span>, -
          <span className="num">1.97916</span>,{" "}
          <span className="num">0.98184</span>]){" "}
          <span className="kw">AS</span> bpfi
          {"\n"}
          <span className="kw">FROM</span> vibration_raw;
        </>
      ),
      out: { label: "kurtosis_trend.m4", value: "2.92e-5", delta: "+9.31e-7" },
    },
    {
      title: "Pump cavitation",
      blurb: "High-frequency vibration conditioning followed by envelope peak detection.",
      sql: (
        <>
          <span className="cmt">-- condition, then detect bursts</span>
          {"\n"}
          <span className="kw">CREATE MATERIALIZED VIEW</span> pump_band{" "}
          <span className="kw">AS</span>
          {"\n"}
          <span className="kw">SELECT</span> pump_id, channel,
          {"\n"}
          {"       "}
          <span className="fn">FIR</span>(accel,{" "}
          <span className="kw">ARRAY</span>[
          <span className="num">0.02</span>, -
          <span className="num">0.05</span>,{" "}
          <span className="num">0.10</span>, -
          <span className="num">0.15</span>,{" "}
          <span className="num">0.18</span>,
          {"\n"}
          {"                    "}-
          <span className="num">0.15</span>,{" "}
          <span className="num">0.10</span>, -
          <span className="num">0.05</span>,{" "}
          <span className="num">0.02</span>]){" "}
          <span className="kw">AS</span> band
          {"\n"}
          <span className="kw">FROM</span> pump_accel;{"\n\n"}
          <span className="kw">CREATE MATERIALIZED VIEW</span> cavitation{" "}
          <span className="kw">AS</span>
          {"\n"}
          <span className="kw">SELECT</span> pump_id, channel,
          {"\n"}
          {"       "}
          <span className="fn">PEAK_DETECT</span>(
          <span className="fn">ABS</span>(band),{" "}
          <span className="num">50</span>){" "}
          <span className="kw">AS</span> burst_peak
          {"\n"}
          <span className="kw">FROM</span> pump_band;
        </>
      ),
      out: { label: "pump-7 · ch2", value: "0.34 g", delta: "burst @ 11:42:19" },
    },
  ];

  return (
    <section className="section" id="what">
      <div className="section-inner">
        <div className="section-header">
          <div className="eyebrow">01 · What you can build</div>
          <h2>Real examples, bundled as presets.</h2>
          <p className="lede">
            Real scripts for real tag workloads. Read the SQL and map it to a calculation you
            already maintain today.
          </p>
        </div>
        <div className="examples-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
          {cards.map((c, i) => (
            <div
              key={i}
              className="card"
              style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>
                  {c.title}
                </div>
                <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>{c.blurb}</div>
              </div>
              <pre
                className="sql"
                style={{
                  flex: 1,
                  fontSize: 10.7,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {c.sql}
              </pre>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  borderTop: "1px solid var(--line)",
                  paddingTop: 10,
                  flexWrap: "wrap",
                }}
              >
                <span
                  className="mono"
                  style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.06em" }}
                >
                  OUT
                </span>
                <span className="mono" style={{ fontSize: 11, color: "var(--ink-2)" }}>
                  {c.out.label}
                </span>
                <span
                  className="mono tnum"
                  style={{ fontSize: 15, color: "var(--trend)", marginLeft: "auto" }}
                >
                  {c.out.value}
                </span>
                <span className="mono" style={{ fontSize: 10, color: "var(--ok)" }}>
                  {c.out.delta}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 16 }}>
          SQL excerpts, shortened for the page from the same operator patterns used by the presets
          and notebooks.
        </div>
      </div>
    </section>
  );
}
